import { Grid, Typography } from "@mui/material"
import { faArrowUpRightFromSquare } from "@fortawesome/pro-light-svg-icons"
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { enqueueSnackbar } from "notistack"
import { makeStyles } from "tss-react/mui"
import { useState } from "react"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import {
    FetchAcquirersResponse,
    acceptTermsAndConditions,
    createAcquirerAgreement,
    fetchAcquirersForSupplier,
    POSQuery,
} from "../SupplierAdminPOSApi"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { WellknownAgreementType } from "../SupplierAdminPOSTypes"
import { ConfirmModal } from "../../../../../../shared/components/dialogs/ConfirmModal"
import { useExternalLinks } from "../../../../../../shared/hooks/useAppLinks"
import { useCurrentSupplier } from "../../../../../suppliers/supplierHooks"
import { LoadingCheckbox } from "../../../../../../shared/components/checkbox/LoadingCheckbox"
import { useLocalizedUrl } from "../../../../../localization/localizationHooks"
import { ExternalLink } from "../../../../../../shared/components/links/ExternalLink"

type SupplierAcquiringNoAgreementProps = Readonly<{
    accepted: boolean
}>

const SALES_TERMS_REQUIREMENTS_DA_URL = "https://support.facilitynet.dk/hc/da/articles/17654349238802"
const SALES_TERMS_REQUIREMENTS_EN_URL = "https://support.facilitynet.dk/hc/en-dk/articles/17654349238802"
const SALES_TERMS_TEMPLATE_URL = "https://www.example.com/t-and-c-template"

const useStyles = makeStyles()((theme) => ({
    title: {
        color: theme.palette.tailwind.red[500],
        fontWeight: 600,
    },
    infoBox: {
        display: "grid",
        gridTemplateColumns: "1rem 1fr",
        gap: "0.75rem",
        color: "#014361",
        background: "#E5F6FD",
        padding: "1rem",
        borderRadius: "0.25rem",
    },
    infoBoxIcon: {
        color: "#0288D1",
        marginTop: "0.15rem",
    },
    boxTitle: {
        fontWeight: 500,
        paddingBottom: "0.5rem",
    },
    linkText: {
        color: "#2196F3",
        cursor: "pointer",
    },
}))

function selectAcquirerForPos(data: FetchAcquirersResponse) {
    return data.acquirers.find((acquirer) => acquirer.agreementTypes.includes("POS"))
}

export function SupplierAcquiringNoAgreement({ accepted }: SupplierAcquiringNoAgreementProps) {
    const { supplierId } = useCurrentSupplier()
    const { l10n } = useAppLocalization()
    const { classes } = useStyles()

    const [checked, setChecked] = useState(accepted)
    const [confirmModalState, setConfirmModalState] = useState(false)

    const salesTermsRequirementsUrl = useLocalizedUrl(
        {
            "da-DK": SALES_TERMS_REQUIREMENTS_DA_URL,
        },
        {
            defaultUrl: SALES_TERMS_REQUIREMENTS_EN_URL,
        },
    )

    const acquirersResult = useApiQuery(fetchAcquirersForSupplier, {
        queryName: POSQuery.ACQUIRERS_DETAILS,
        dependencies: {
            supplierId,
        },
        select: selectAcquirerForPos,
    })

    const { mutate: callCreateAcquirerAgreement, isLoading: isCreatingAcquirer } = useApiMutation(
        createAcquirerAgreement,
        {
            async onSuccess(_params, data, _context) {
                callAcceptTermsAndConditions({
                    supplierId: supplierId,
                    agreementId: data.agreement.id,
                })
            },
        },
    )

    const { mutate: callAcceptTermsAndConditions, isLoading: isAcceptingTerms } = useApiMutation(
        acceptTermsAndConditions,
        {
            async onSuccess(_params, _data, context) {
                setChecked(true)
                await context.queryClient.invalidateQueries({ queryKey: [POSQuery.AGREEMENTS_DETAILS] })
            },
        },
    )

    const handleAccept = () => {
        if (acquirersResult.data) {
            callCreateAcquirerAgreement({
                agreement: {
                    type: WellknownAgreementType.ADYEN_POS,
                    supplier: {
                        id: supplierId,
                    },
                    acquirer: {
                        id: acquirersResult.data.id,
                    },
                },
            })
        } else {
            enqueueSnackbar({
                variant: "error",
                message: l10n.getString("supplier-admin-pos-acquirer-status-no-payment-error"),
            })
        }
    }
    const handleExternalLink = useExternalLinks()

    return (
        <>
            <LocalizedStrict id="supplier-admin-pos-acquirer-status-no-payment-title">
                <Typography variant="body1" className={classes.title}>
                    You have no payment acquiring agreement.
                </Typography>
            </LocalizedStrict>
            <Grid className={classes.infoBox}>
                <FontAwesomeIcon icon={faCircleInfo} className={classes.infoBoxIcon} />
                <Grid container direction="column">
                    <LocalizedStrict id="supplier-admin-acquiring-terms-and-conditions-title">
                        <Typography variant="body1" className={classes.boxTitle}>
                            Terms and conditions
                        </Typography>
                    </LocalizedStrict>
                    <LocalizedStrict
                        id="supplier-admin-acquiring-terms-and-conditions-explanation"
                        elems={{
                            requirementsLink: (
                                <ExternalLink
                                    href={salesTermsRequirementsUrl}
                                    className={classes.linkText}
                                ></ExternalLink>
                            ),
                        }}
                    >
                        <Typography variant="body1">
                            To proceed, you must agree to Adyen's requirements for your{" "}
                            <ExternalLink href={salesTermsRequirementsUrl} className={classes.linkText}>
                                Terms and Conditions of Sale
                            </ExternalLink>
                            . These terms and conditions must be clearly visible for your customers on your website.
                        </Typography>
                    </LocalizedStrict>
                    <LocalizedStrict
                        id="supplier-admin-acquiring-terms-and-conditions-template-text"
                        elems={{
                            templateLink: (
                                <ExternalLink
                                    href={SALES_TERMS_TEMPLATE_URL}
                                    className={classes.linkText}
                                ></ExternalLink>
                            ),
                        }}
                    >
                        <Typography variant="body1" marginTop="1rem" marginBottom="0.5rem">
                            You can download our Terms and Conditions template{" "}
                            <ExternalLink href={SALES_TERMS_TEMPLATE_URL} className={classes.linkText}>
                                here
                            </ExternalLink>
                            .
                        </Typography>
                    </LocalizedStrict>
                    <LocalizedStrict id="supplier-admin-acquiring-terms-and-conditions-label" attrs={{ label: true }}>
                        <LoadingCheckbox
                            loading={isCreatingAcquirer || isAcceptingTerms}
                            label="Terms and conditions"
                            checked={checked}
                            disabled={checked}
                            onChange={() => setConfirmModalState((prev) => !prev)}
                        />
                    </LocalizedStrict>
                </Grid>
            </Grid>
            <Grid>
                <LocalizedStrict id="supplier-admin-pos-acquirer-start-application-button">
                    <StandardButton
                        size="medium"
                        fullWidth={false}
                        disabled={true}
                        endIcon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
                        style={{
                            paddingLeft: "32px",
                            paddingRight: "32px",
                        }}
                    >
                        Start application at Adyen
                    </StandardButton>
                </LocalizedStrict>
            </Grid>
            {confirmModalState && (
                <ConfirmModal
                    title={l10n.getString("supplier-admin-acquiring-accept-terms-dialog-title")}
                    message={l10n.getString("supplier-admin-acquiring-accept-terms-dialog-description")}
                    buttonLabelSubmit={l10n.getString("supplier-admin-acquiring-accept-terms-dialog-button")}
                    handleClick={handleAccept}
                    open={confirmModalState}
                    onClose={() => setConfirmModalState(false)}
                />
            )}
        </>
    )
}
